import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch } from 'react-redux';
import { strings } from '../../resources/strings';
import { lensClassFilterList, orderTags } from '../../resources/orderResources';
import { omnichannelResources } from '../../resources/omnichannelResources';
import { MainActions } from '../../actions/MainActions';
import { Field, FieldType } from '../../common-components/Field/Field';
import { Button } from '../../common-components/Button/Button';
import { If } from '../../common-components/If/If';
import { SeparationType } from '../../enums/SeparationType';
import { OrderAlertType } from '../../enums/OrderAlertType';
import { ProfileType } from '../../enums/ProfileType';
import { SessionHelper } from '../../helpers/SessionHelper';
import { orderStates } from '../../services/StatesOrders/StatesService';
import { prescriptionStates } from '../../services/StatesPrescriptions/StatesPrescriptionsService';
import { Seller } from '../../models/Seller';
import { ShopLocation } from '../../models/ShopLocation';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { ContainerBody, Row } from './styles';

export interface IFilterToListOrdersOutput {
  customerName?: string;
  customerEmail?: string;
  specialTag?: string;
  number?: number;
  numbers?: string;
  stateIds?: string;
  statePrescriptionId?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  orderAlertType?: OrderAlertType;
  locationId?: number | null;
  locationName?: string | null;
  sellerId?: number | null;
  sellerName?: string | null;
  stateIdsForUserProfile?: string;
  deliveryDeadlineStartDate?: Date | null;
  deliveryDeadlineEndDate?: Date | null;
  deliveryStartDate?: Date | null;
  deliveryEndDate?: Date | null;
  lensMaterialName?: string | null;
  lensClassName?: string | null;
  fulfilledBy?: string | null;
  originedBy?: string | null;
}

interface IModalFiltersToListOrdersProps {
  appliedFilters?: IFilterToListOrdersOutput;
  separationType?: SeparationType;
  close: () => void;
  finish: (values: IFilterToListOrdersOutput) => void;
}

const ModalFiltersToListOrders: React.FC<IModalFiltersToListOrdersProps> = ({
  appliedFilters,
  separationType,
  close,
  finish
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session, mainLocation } = useAuth();
  const [sellers, setSellers] = React.useState<Seller[]>([]);
  const [sellersWaiting, setSellersWaiting] = React.useState<boolean>(false);
  const [orderNumbers, setOrderNumbers] = React.useState<string | undefined>(appliedFilters?.numbers);
  const [startDate, setStartDate] = React.useState<Date | null>(appliedFilters?.startDate || null);
  const [endDate, setEndDate] = React.useState<Date | null>(appliedFilters?.endDate || null);
  const [stateIds, setStateIds] = React.useState<string | undefined>(appliedFilters?.stateIds);
  const [statePrescriptionId, setStatePrescriptionId] = React.useState<number | undefined>(appliedFilters?.statePrescriptionId);
  const [customerName, setCustomerName] = React.useState<string | undefined>(appliedFilters?.customerName);
  const [customerEmail, setCustomerEmail] = React.useState<string | undefined>(appliedFilters?.customerEmail);
  const [specialTag, setSpecialTag] = React.useState<string | undefined>(appliedFilters?.specialTag);
  const [orderAlertType, setOrderAlertType] = React.useState<OrderAlertType>(appliedFilters?.orderAlertType || OrderAlertType.NoAlert);
  const [locationId, setLocationId] = React.useState<number | undefined | null>(appliedFilters?.locationId);
  const [locationName, setLocationName] = React.useState<string | undefined | null>(appliedFilters?.locationName);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [sellerId, setSellerId] = React.useState<number | undefined | null>(appliedFilters?.sellerId);
  const [sellerName, setSellerName] = React.useState<string | undefined | null>(appliedFilters?.sellerName);
  const [stateIdsForUserProfile, setStateIdsForUserProfile] = React.useState<string | undefined>(appliedFilters?.stateIdsForUserProfile);
  const [deliveryDeadlineStartDate, setDeliveryDeadlineStartDate] = React.useState<Date | null>(appliedFilters?.deliveryDeadlineStartDate || null);
  const [deliveryDeadlineEndDate, setDeliveryDeadlineEndDate] = React.useState<Date | null>(appliedFilters?.deliveryDeadlineEndDate || null);
  const [deliveryStartDate, setDeliveryStartDate] = React.useState<Date | null>(appliedFilters?.deliveryStartDate || null);
  const [deliveryEndDate, setDeliveryEndDate] = React.useState<Date | null>(appliedFilters?.deliveryEndDate || null);
  const [lensMaterialName, setLensMaterialName] = React.useState<string | undefined | null>(appliedFilters?.lensMaterialName || null);
  const [lensClassName, setLensClassName] = React.useState<string | undefined | null>(appliedFilters?.lensClassName || null);
  const [fulfilledBy, setFulfilledBy] = React.useState<string | undefined | null>(appliedFilters?.fulfilledBy);
  const [originedBy, setOriginedBy] = React.useState<string | undefined | null>(appliedFilters?.originedBy);

  const {
    getActiveSellersByLocationsService,
    statesService,
    getLocationListService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getSellers();
    getLocations();
  }, []);

  const sellerFilter = React.useMemo(() => {
    if (!sellers || sellers.length <= 0) {
      if (sellersWaiting) {
        return (
          <Row>
            <Field
              type={FieldType.Select}
              label={strings.Seller}
              selectProps={{
                options: [
                  { id: 0, name: 'Carregando...' },
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={0}
              marginTop={'0px'}
              disabled={true}
            />
          </Row>
        );
      }
      return null;
    }
    return (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.Seller}
          selectProps={{
            options: [
              { id: 0, name: strings.All },
              ...sellers
            ],
            displayField: 'name',
            keyField: 'id'
          }}
          value={sellerId}
          change={(value) => setSeller(sellers.find(s => s.id === +value))}
          marginTop={'0px'}
        />
      </Row>
    );
  }, [sellerId, sellers, sellerName, appliedFilters, sellersWaiting]);

  const filterStateOrderRender = SessionHelper.canFilterStates(session) && (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.StateOfOrder}
        selectProps={{
          options: [
            { id: 0, name: strings.All },
            ...statesService.translateListByUserProfile(statesService.filterByUserProfile(orderStates, session?.user?.profileType), session?.user?.profileType)
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={stateIds ? stateIds.split(' ')[0] : undefined}
        change={(value) => setStateIds(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const filterStatePrescriptionRender = SessionHelper.canFilterPrescriptionStates(session) && separationType === SeparationType.Prescription ? (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.StateOfDegree}
        selectProps={{
          options: [{ id: 0, description: strings.All }, ...prescriptionStates],
          displayField: 'description',
          keyField: 'id'
        }}
        value={statePrescriptionId}
        change={(value) => setStatePrescriptionId(value)}
        marginTop={'0px'}
      />
    </Row>
  ) : null;

  const filterStateOrderForUserProfileRender = (session?.user?.profileType === ProfileType.GlobalPicker) &&
    (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.StateOfOrder}
          selectProps={{
            options: [
              { id: 0, name: strings.All },
              ...(statesService.getByUserProfileAndSeparationType(session, separationType) || [])
            ],
            displayField: 'name',
            keyField: 'id'
          }}
          value={stateIdsForUserProfile ? stateIdsForUserProfile.split(' ')[0] : undefined}
          change={(value) => setStateIdsForUserProfile(value)}
          marginTop={'0px'}
        />
      </Row>
    );

  const filterSpecialTagAndAlertRender = (SessionHelper.canFilterSpecialTagAndAlert(session)) && (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.SpecialTag}
        selectProps={{
          options: [
            { id: '', name: strings.All },
            { id: orderTags.influenciadores.value, name: orderTags.influenciadores.display },
            { id: orderTags.amaro.value, name: orderTags.amaro.display },
            { id: orderTags.non_progressive_order_bf2023.value, name: orderTags.non_progressive_order_bf2023.display },
            { id: orderTags.externally_changed.value, name: orderTags.externally_changed.display },
            { id: orderTags.pre_sale_sport.value, name: orderTags.pre_sale_sport.display }
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={specialTag}
        change={(value) => setSpecialTag(value)}
        marginTop={'0px'}
        marginRight='5px'
      />
    </Row>
  );

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header style={{ height: '50px' }}>
        <Modal.Title>
          Filtrar Pedidos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <ContainerBody>
          <Row>
            <Field
              type={FieldType.Text}
              label={strings.Orders}
              value={orderNumbers}
              change={(value) => changeOrderNumbers(value)}
              replaceEnterCodeByValue={' '}
              autoFocus={true}
              marginTop={'0px'}
            />
          </Row>

          <If condition={session?.user?.profileType === ProfileType.Vendor}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.FulfilledBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStore.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStore.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.Ecommerce.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={fulfilledBy}
                change={(value) => setFulfilledBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>

          <If condition={session?.user?.profileType === ProfileType.Ecommerce}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.OriginatedBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.Ecommerce.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={originedBy}
                change={(value) => setOriginedBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>

          <If condition={userLocations.length > 1 && !loadingLocations}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.Location}
                selectProps={{
                  options: [{ id: 0, name: strings.All }, ...locations.filter(l => userLocations.some(ulid => ulid === l.id))],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={locationId}
                change={(value) => setLocation(locations.find(l => l.id === +value))}
                marginTop={'0px'}
              />
            </Row>
          </If>
          <Row>
            <Field
              type={FieldType.DatePicker}
              label={strings.PaidOrderOf}
              marginRight='5px'
              value={startDate}
              change={(value) => setStartDate(value)}
              marginTop={'0px'}
            />
            <Field
              type={FieldType.DatePicker}
              label={strings.PaidOrderUntil}
              marginLeft='5px'
              value={endDate}
              change={(value) => setEndDate(value)}
              marginTop={'0px'}
            />
          </Row>
          {SessionHelper.canFilterCustomerDeadline(session) ?
            <Row>
              <Field
                type={FieldType.DatePicker}
                label={strings.CustomerDeadlineOf}
                marginRight='5px'
                value={deliveryDeadlineStartDate}
                change={(value) => setDeliveryDeadlineStartDate(value)}
                marginTop={'0px'}
              />
              <Field
                type={FieldType.DatePicker}
                label={strings.CustomerDeadlineUntil}
                marginLeft='5px'
                value={deliveryDeadlineEndDate}
                change={(value) => setDeliveryDeadlineEndDate(value)}
                marginTop={'0px'}
              />
            </Row>
            : null}
          {SessionHelper.canFilterDeliveredOrder(session) ?
            <Row>
              <Field
                type={FieldType.DatePicker}
                label={strings.DeliveredOf}
                marginRight='5px'
                value={deliveryStartDate}
                change={(value) => setDeliveryStartDate(value)}
                marginTop={'0px'}
              />
              <Field
                type={FieldType.DatePicker}
                label={strings.DeliveredUntil}
                marginLeft='5px'
                value={deliveryEndDate}
                change={(value) => setDeliveryEndDate(value)}
                marginTop={'0px'}
              />
            </Row>
            : null}
          {filterStateOrderRender}
          {filterStatePrescriptionRender}
          {filterStateOrderForUserProfileRender}
          {SessionHelper.canFilterCustomer(session) ?
            <Row>
              <Field
                type={FieldType.Text}
                label={strings.Client}
                value={customerName}
                change={(value) => setCustomerName(value)}
                marginTop={'0px'}
                marginRight='5px'
              />
              <Field
                type={FieldType.Text}
                label={strings.Email}
                value={customerEmail}
                change={(value) => setCustomerEmail(value)}
                marginTop={'0px'}
                marginLeft='5px'
              />
            </Row>
            : null}
          {filterSpecialTagAndAlertRender}
          {sellerFilter}
          {separationType === SeparationType.Prescription && SessionHelper.canFilterLensMaterial(session) ?
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.LensMaterial}
                selectProps={{
                  options: [
                    { id: null, name: strings.All },
                    ...lensClassFilterList.map(name => ({ id: name, name: name }))
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={lensClassName}
                change={(value) => setLensClassName(value)}
                marginTop={'0px'}
              />
            </Row>
            : null}
        </ContainerBody>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button text={strings.Cancel} click={() => close()} marginRight={'10px'} />
          <Button text={strings.Confirm} click={() => filter()} marginLeft={'10px'} />
        </div>
      </Modal.Footer>
    </Modal>
  );

  function changeOrderNumbers(value: string) {
    setOrderNumbers(value);
  }

  function filter() {
    finish({
      numbers: orderNumbers,
      startDate: startDate,
      endDate: endDate,
      stateIds: stateIds,
      statePrescriptionId: statePrescriptionId,
      customerName: customerName,
      customerEmail: customerEmail,
      specialTag: specialTag,
      orderAlertType: +orderAlertType,
      locationId: locationId,
      locationName: locationName,
      sellerId: sellerId,
      sellerName: sellerName,
      stateIdsForUserProfile: stateIdsForUserProfile,
      deliveryDeadlineStartDate: deliveryDeadlineStartDate,
      deliveryDeadlineEndDate: deliveryDeadlineEndDate,
      deliveryStartDate: deliveryStartDate,
      deliveryEndDate: deliveryEndDate,
      lensMaterialName: lensMaterialName,
      lensClassName: lensClassName,
      fulfilledBy: fulfilledBy,
      originedBy: originedBy
    });
  }

  function getSellers() {
    if (!SessionHelper.canFilterSeller(session)) return;
    setSellersWaiting(true);
    mainActions.setNotWait(true);
    getActiveSellersByLocationsService.handle(userLocations.join(','))
      .then(result => {
        setSellers(result?.value?.sellers || []);
        setSellersWaiting(false);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
      });
  }

  function setSeller(seller?: Seller | null) {
    setSellerId(seller?.id);
    setSellerName(seller?.name);
  }

  function getLocations() {
    const manyLocations = userLocations.length > 1;
    if (!manyLocations) return;
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

  function setLocation(location?: ShopLocation | null) {
    setLocationId(location?.id);
    setLocationName(location?.name);
  }
}

export { ModalFiltersToListOrders };